"use client";

import { clearParkedTeamIntent } from "@/components/ui/Cteamsgallery/gallery.copy-intent";
import { useTeamCrafter } from "@/contexts/team-crafter";
import type { HomePageControllerLocal } from "./domain/home-page/types";
import { getHomePageController } from "./homePageControllerModel";
import { getPostLoginAuthReady } from "./homePageModalSelectors";
import { useAllTeamsGalleryChooserRegistration } from "./useAllTeamsGalleryChooserRegistration";
import { useAuthReturnRouteResume } from "./useAuthReturnRouteResume";
import { useHomePageUrlRestores, useTeamModalUrlSync } from "./homePageUrl";
import { useHomePageControllerHandlers } from "./useHomePageControllerHandlers";
import { useHomePageFooterHandlers } from "./useHomePageFooterHandlers";
import {
  useHomePageFlickerProbe,
} from "./useHomePageTelemetry";
import { useHomePageModalDebugState } from "./useHomePageModalDebugState";
import { useHomePageRuntime } from "./useHomePageRuntime";
import { useLandingPrepage } from "./useLandingPrepage";
import { useLatestInteractionState } from "./useLatestInteractionState";
import { usePostLoginIntentRestore } from "./postLoginIntent";

export function useHomePageController(): HomePageControllerLocal {
  const runtime = useHomePageRuntime();
  const modalDebugState = useHomePageModalDebugState({
    authLoading: runtime.loading,
    hasOpenedTeamDetailsModal: runtime.modalState.hasOpenedTeamDetailsModal,
    hasOpenedTeamsModal: runtime.modalState.hasOpenedTeamsModal,
    isTeamGalleryCoveredByDetails:
      runtime.modalState.isTeamGalleryCoveredByDetails,
    modalDebugLogger: runtime.modalDebugLogger,
    showAuthModal: runtime.modalState.showAuthModal,
    showPricingModal: runtime.modalState.showPricingModal,
    showTeamDetailsModal: runtime.modalState.showTeamDetailsModal,
    showTeamsModal: runtime.modalState.showTeamsModal,
    teamDetailsTeamId: runtime.modalState.teamDetailsTeamId,
    userId: runtime.userId,
    viewport: runtime.viewport,
    viewportLoaded: runtime.viewportLoaded,
  });
  const latestInteractionState = useLatestInteractionState({
    showTeamsModal: runtime.modalState.showTeamsModal,
    showTeamDetailsModal: runtime.modalState.showTeamDetailsModal,
    teamDetailsTeamId: runtime.modalState.teamDetailsTeamId,
    isAuthenticated: runtime.isAuthenticated,
  });
  const handlers = useHomePageControllerHandlers({
    latestInteractionState,
    runtime,
  });
  const isPostLoginAuthReady = getPostLoginAuthReady(modalDebugState);
  const { openTeamCrafter } = useTeamCrafter();

  usePostLoginIntentRestore({
    authLoading: modalDebugState.authLoading,
    isAuthReady: isPostLoginAuthReady,
    modalDebugLogger: runtime.modalDebugLogger,
    openDeployForTeam: handlers.openDeployForTeam,
    // The gated build handler: restore runs post-auth, so the gate passes
    // straight through to the canonical modal's empty subject.
    openNewTeamEditor: handlers.handleBuildYourOwnTeam,
    // The Crafter restores ungated (post-auth): create opens it outright,
    // edit reopens the team's editor first through the same dispatcher the
    // `?viewTeam&mode=` grammar uses, then opens the Crafter on it.
    openTeamEditor: handlers.openTeamEditor,
    openTeamCrafter,
    startPlanCheckoutFlow: handlers.startPlanCheckoutFlow,
    userId: modalDebugState.userId,
  });

  useAuthReturnRouteResume({
    authLoading: runtime.loading,
    isAuthenticated: runtime.isAuthenticated,
    openAuthModal: runtime.modalState.openAuthModal,
    // The landing's one parked team action, held across a sign-in redirect.
    // Named here rather
    // than inside the hook: this is the layer that knows the landing hosts the
    // teams gallery. Imported from the leaf module, not the gallery barrel,
    // which `LandingPageModalLayer` deliberately loads dynamically.
    releaseParkedIntents: clearParkedTeamIntent,
  });

  useHomePageUrlRestores({
    isAuthenticated: runtime.isAuthenticated,
    loading: runtime.loading,
    modalDebugLogger: runtime.modalDebugLogger,
    // The gallery arrives by the same handler the hero CTA fires, whether the
    // request came over the URL or straight off a mounted chooser.
    openAllTeamsGallery: handlers.handleAllTeamsClick,
    openDeployForTeam: handlers.openDeployForTeam,
    openTeamDetails: handlers.openTeamDetails,
    openTeamEditor: handlers.openTeamEditor,
  });

  // The chooser's "Existing team" option, taken while the landing is already
  // mounted: it opens the gallery in place instead of navigating.
  useAllTeamsGalleryChooserRegistration({
    openAllTeamsGallery: handlers.handleAllTeamsClick,
  });

  // The landing is the ONE surface that mirrors the team modal into the URL.
  useTeamModalUrlSync();

  const footerHandlers = useHomePageFooterHandlers();
  const prepageState = useLandingPrepage({
    landingPerfPrepageLog: runtime.landingPerfPrepageLog,
    viewportLoaded: runtime.viewportLoaded,
  });

  useHomePageFlickerProbe({
    hasOpenedTeamDetailsModal: runtime.modalState.hasOpenedTeamDetailsModal,
    loading: runtime.loading,
    prepageMinElapsed: prepageState.prepageMinElapsed,
    shouldRenderPrepage: prepageState.shouldRenderPrepage,
    showAuthModal: runtime.modalState.showAuthModal,
    showPrepage: prepageState.showPrepage,
    showPricingModal: runtime.modalState.showPricingModal,
    showTeamDetailsModal: runtime.modalState.showTeamDetailsModal,
    showTeamsModal: runtime.modalState.showTeamsModal,
    teamDetailsTeamId: runtime.modalState.teamDetailsTeamId,
    userId: runtime.userId,
    viewport: runtime.viewport,
    viewportLoaded: runtime.viewportLoaded,
  });

  return getHomePageController({
    footerHandlers,
    handlers,
    prepageState,
    runtime,
  });
}
